import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useGetConsolidatedReportMutation } from '../Services/modules/exam';
import { useGetAllAgentsQuery } from '../Services/modules/agents';
import { Config } from '../Config';

const TSE_NAME = 'गुणवत्ता शोध परीक्षा';
const SSP_NAME = 'शताब्दी शिष्यवृत्ती परीक्षा';

const EXAM_COLUMNS = [
  { standard: '1', examName: TSE_NAME, label: '1st TSE' },
  { standard: '2', examName: TSE_NAME, label: '2nd TSE' },
  { standard: '3', examName: TSE_NAME, label: '3rd TSE' },
  { standard: '5', examName: TSE_NAME, label: '5th TSE' },
  { standard: '6', examName: TSE_NAME, label: '6th TSE' },
  { standard: '8', examName: TSE_NAME, label: '8th TSE' },
  { standard: '4', examName: SSP_NAME, label: '4th SSP' },
  { standard: '7', examName: SSP_NAME, label: '7th SSP' },
];

const YEARS = ['2024', '2025', '2026'];

const getCount = (entries, standard, examName) => {
  const entry = entries?.find(
    (e) => e.exam_standard === standard && e.exam_name === examName
  );
  return entry ? entry.count : 0;
};

function ConsolidatedReport({ agentId }) {
  const userData = useSelector((state) => state.userData.user_data);
  const isAdmin = userData?.role === 'admin';
  const token = userData?.token;

  const [year, setYear] = useState('2025');
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [schoolFilter, setSchoolFilter] = useState('');
  const [rows, setRows] = useState([]);
  const [agents, setAgents] = useState([]);
  const [isExporting, setIsExporting] = useState(false);

  const [GetConsolidatedReport, { data, isSuccess, isLoading }] =
    useGetConsolidatedReportMutation();

  // Fetch agent list — only for admin and when no specific agentId is pinned
  const showAgentDropdown = isAdmin && !agentId;
  const { data: agentsData, isSuccess: agentsSuccess } = useGetAllAgentsQuery(
    undefined,
    { skip: !showAgentDropdown }
  );

  useEffect(() => {
    if (agentsSuccess && agentsData?.data) {
      setAgents(agentsData.data);
    }
  }, [agentsData, agentsSuccess]);

  const fetchReport = (overrideYear, overrideAgentId) => {
    const effectiveYear = overrideYear ?? year;
    const effectiveAgent = agentId || (overrideAgentId !== undefined ? overrideAgentId : selectedAgentId);
    const payload = { year: effectiveYear };
    if (effectiveAgent) payload.agentId = effectiveAgent;
    GetConsolidatedReport(payload);
  };

  useEffect(() => {
    fetchReport();
  }, []);

  useEffect(() => {
    if (isSuccess && data?.data) {
      setRows(data.data);
    }
  }, [data, isSuccess]);

  const handleYearChange = (e) => {
    setYear(e.target.value);
    fetchReport(e.target.value, selectedAgentId);
  };

  const handleAgentChange = (e) => {
    setSelectedAgentId(e.target.value);
    fetchReport(year, e.target.value);
  };

  // Client-side school filter (supports Marathi unicode)
  const filteredRows = schoolFilter
    ? rows.filter((row) =>
        row._id?.school_name?.toLowerCase().includes(schoolFilter.toLowerCase()) ||
        row._id?.school_name?.includes(schoolFilter)
      )
    : rows;

  const colTotals = EXAM_COLUMNS.map((col) =>
    filteredRows.reduce((sum, row) => sum + getCount(row.entries, col.standard, col.examName), 0)
  );
  const grandTotal = filteredRows.reduce((sum, row) => sum + row.row_total, 0);

  const exportExcel = () => {
    setIsExporting(true);
    const effectiveAgent = agentId || selectedAgentId;
    const payload = { year };
    if (effectiveAgent) payload.agentId = effectiveAgent;

    fetch(`${Config.API_URL_LOCAL}exam/getConsolidatedReportExcel`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    })
      .then((res) => {
        if (!res.ok) throw new Error('Export failed');
        const filename = res.headers.get('X-File-Name') || 'consolidated-report.xlsx';
        return res.blob().then((blob) => ({ filename, blob }));
      })
      .then(({ filename, blob }) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        window.URL.revokeObjectURL(url);
      })
      .catch((err) => console.error('Export error:', err))
      .finally(() => setIsExporting(false));
  };

  return (
    <div className="card mt-4">
      <div className="card-body">
        <h5 className="mb-3">Consolidated Report</h5>

        <div className="row mb-3 g-2 align-items-end">
          {/* Agent dropdown — admin only, when not pinned to a specific agent */}
          {showAgentDropdown && (
            <div className="col-md-4">
              <label className="form-label mb-1">Agent</label>
              <select
                className="form-select"
                value={selectedAgentId}
                onChange={handleAgentChange}
                disabled={isLoading}
              >
                <option value="">All Agents</option>
                {agents.map((a) => (
                  <option key={a._id} value={a._id}>
                    {a.full_name} ({a.agent_id})
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Year filter */}
          <div className="col-md-2">
            <label className="form-label mb-1">Year</label>
            <select
              className="form-select"
              value={year}
              onChange={handleYearChange}
              disabled={isLoading}
            >
              {YEARS.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>

          {/* School filter (client-side) */}
          <div className="col-md-4">
            <label className="form-label mb-1">School Filter</label>
            <input
              type="text"
              className="form-control"
              placeholder="शाळेचे नाव टाइप करा..."
              value={schoolFilter}
              onChange={(e) => setSchoolFilter(e.target.value)}
            />
          </div>

          {/* Export button */}
          <div className="col-md-2">
            <button
              className="btn btn-success w-100"
              onClick={exportExcel}
              disabled={isExporting || rows.length === 0}
            >
              {isExporting ? 'Exporting...' : 'Export Excel'}
            </button>
          </div>
        </div>

        {isLoading ? (
          <p>Loading report...</p>
        ) : filteredRows.length === 0 ? (
          <p className="text-muted">No data found.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="table table-bordered table-sm" style={{ minWidth: '900px' }}>
              <thead className="table-primary">
                <tr>
                  <th>School Name</th>
                  <th>Medium</th>
                  {EXAM_COLUMNS.map((col) => (
                    <th key={col.label} className="text-center">{col.label}</th>
                  ))}
                  <th className="text-center">Total</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row, idx) => (
                  <tr key={idx}>
                    <td>{row._id?.school_name}</td>
                    <td>{row._id?.exam_medium}</td>
                    {EXAM_COLUMNS.map((col) => (
                      <td key={col.label} className="text-center">
                        {getCount(row.entries, col.standard, col.examName) || ''}
                      </td>
                    ))}
                    <td className="text-center fw-bold">{row.row_total}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="table-warning fw-bold">
                  <td>TOTAL</td>
                  <td></td>
                  {colTotals.map((t, i) => (
                    <td key={i} className="text-center">{t || ''}</td>
                  ))}
                  <td className="text-center">{grandTotal}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default ConsolidatedReport;
